import React from 'react';
import { motion } from 'framer-motion';

interface GlowButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'small' | 'medium' | 'large' | 'huge';
  className?: string;
  disabled?: boolean;
}

const GlowButton: React.FC<GlowButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'large',
  className = '',
  disabled = false,
}) => {
  const variants = {
    primary: 'bg-army-olive hover:bg-army-olive-light border-army-gold text-white',
    secondary: 'bg-army-dark hover:bg-army-black border-army-gold text-army-gold',
    danger: 'bg-red-900 hover:bg-red-800 border-red-500 text-white',
  };

  const sizes = {
    small: 'px-4 py-2 md:px-4 md:py-2 lg:px-4 lg:py-2 text-sm md:text-sm lg:text-sm 4k:text-4xl 4k:px-12 4k:py-8',
    medium: 'px-5 py-2.5 md:px-5 md:py-2.5 lg:px-6 lg:py-3 text-base md:text-base lg:text-base 4k:text-5xl 4k:px-16 4k:py-10',
    large: 'px-6 py-3 md:px-7 md:py-3 lg:px-8 lg:py-3.5 text-base md:text-base lg:text-lg 4k:text-6xl 4k:px-24 4k:py-12',
    huge: 'px-7 py-3.5 md:px-8 md:py-4 lg:px-10 lg:py-4 text-lg md:text-lg lg:text-xl 4k:text-8xl 4k:px-32 4k:py-16',
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`
        relative overflow-hidden
        ${variants[variant]}
        ${sizes[size]}
        border-3 4k:border-12
        font-bold font-tactical uppercase tracking-wider
        transition-all duration-300
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      whileHover={!disabled ? { scale: 1.05 } : {}}
      whileTap={!disabled ? { scale: 0.95 } : {}}
    >
      {/* Glow effect */}
      {!disabled && (
        <>
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20"
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 1,
            }}
          />
          <motion.div
            className="absolute inset-0"
            animate={{
              boxShadow: [
                '0 0 20px rgba(212, 175, 55, 0)',
                '0 0 40px rgba(212, 175, 55, 0.3)',
                '0 0 20px rgba(212, 175, 55, 0)',
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
            }}
          />
        </>
      )}

      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-3 h-3 4k:w-12 4k:h-12 border-t-3 border-l-3 4k:border-t-12 4k:border-l-12 border-white opacity-50" />
      <div className="absolute top-0 right-0 w-3 h-3 4k:w-12 4k:h-12 border-t-3 border-r-3 4k:border-t-12 4k:border-r-12 border-white opacity-50" />
      <div className="absolute bottom-0 left-0 w-3 h-3 4k:w-12 4k:h-12 border-b-3 border-l-3 4k:border-b-12 4k:border-l-12 border-white opacity-50" />
      <div className="absolute bottom-0 right-0 w-3 h-3 4k:w-12 4k:h-12 border-b-3 border-r-3 4k:border-b-12 4k:border-r-12 border-white opacity-50" />

      {/* Button content */}
      <span className="relative z-10">{children}</span>
    </motion.button>
  );
};

export default GlowButton;